import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EventIcon from "@mui/icons-material/Event";
import {
  adjustTimes,
  getStopDepartureDatetime,
  getStopArrivalDatetime,
} from "../utils/DateTime";
import { getCityDisplay, cityMatchesSearch } from "../utils/city";
import MapAddressLink from "./MapAddressLink";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const getLegSegment = (leg) => {
  const departureDateTime = dayjs(leg.trip.departure_date);
  const stops = adjustTimes(departureDateTime, leg.trip.route?.stops ?? []);
  const fromIdx = stops.findIndex((stop) => cityMatchesSearch(stop.city, leg.from_city));
  const toIdx = stops.findIndex((stop) => cityMatchesSearch(stop.city, leg.to_city));
  if (fromIdx === -1 || toIdx === -1 || fromIdx >= toIdx) return null;
  const fromStop = stops[fromIdx];
  const toStop = stops[toIdx];
  if (!getStopDepartureDatetime(fromStop) || !getStopArrivalDatetime(toStop)) return null;
  return { fromStop, toStop };
};

const formatDuration = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

const formatLayover = (minutes, t) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) {
    return t("transferCard.layoverHoursMinutes", { hours, minutes: mins });
  }
  if (hours > 0) {
    return t("transferCard.layoverHours", { hours });
  }
  return t("transferCard.layoverMinutes", { minutes: mins });
};

const TransferItineraryCard = ({
  itinerary,
  fromCity,
  toCity,
  adults,
  children,
  onSelect,
}) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const leg1 = itinerary.legs?.find((l) => l.leg_index === 1) ?? itinerary.legs?.[0];
  const leg2 = itinerary.legs?.find((l) => l.leg_index === 2) ?? itinerary.legs?.[1];
  if (!leg1 || !leg2) return null;

  const segment1 = getLegSegment(leg1);
  const segment2 = getLegSegment(leg2);
  if (!segment1 || !segment2) return null;

  const departAt = getStopDepartureDatetime(segment1.fromStop);
  const arriveAt = getStopArrivalDatetime(segment2.toStop);
  const totalDurationText = formatDuration(itinerary.total_duration_minutes);
  const priceUah = Math.round(itinerary.total_price_uah ?? 0);
  const hubName = getCityDisplay(itinerary.hub_city);
  const layoverText = formatLayover(itinerary.layover_minutes, t);
  const carrier1 = leg1.trip?.route?.carrier;
  const carrier2 = leg2.trip?.route?.carrier;

  const handleSelect = () => {
    if (onSelect) {
      onSelect({
        type: "transfer",
        itinerary,
        fromCity,
        toCity,
        adults,
        children,
      });
      return;
    }
    navigate("/checkout", {
      state: {
        type: "transfer",
        itinerary,
        fromCity,
        toCity,
        adults,
        children,
      },
    });
  };

  return (
    <Card
      component="article"
      aria-label={t("tripCard.ariaLabel", {
        time: departAt.format("HH:mm"),
        endTime: arriveAt.format("HH:mm"),
        price: priceUah,
      })}
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          boxShadow: theme.shadows[4],
          borderColor: theme.palette.primary.light,
        },
      }}
    >
      <CardContent
        sx={{
          p: { xs: 2, sm: 3 },
          "&:last-child": { pb: { xs: 2, sm: 3 } },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 2.5, sm: 3 },
          }}
        >
          {/* Відправлення */}
          <Box sx={{ flex: { sm: 1 }, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography
                component="span"
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {departAt.format("HH:mm")}
              </Typography>
              <Chip
                size="small"
                icon={<EventIcon sx={{ fontSize: 14 }} />}
                label={departAt.format("DD.MM.YYYY")}
                sx={{
                  height: 24,
                  fontSize: "0.75rem",
                  bgcolor: "action.hover",
                  "& .MuiChip-icon": { color: "primary.main" },
                }}
              />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 0.5 }}>
              {getCityDisplay(segment1.fromStop.city)}
            </Typography>
            {segment1.fromStop.address && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.8125rem" }}
              >
                <MapAddressLink
                  city={getCityDisplay(segment1.fromStop.city)}
                  address={segment1.fromStop.address}
                />
              </Typography>
            )}
          </Box>

          {/* Маршрут: тривалість + пересадка */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flex: { sm: "0 0 auto" },
              width: { xs: "100%", sm: 160 },
            }}
          >
            <Chip
              size="small"
              icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
              label={totalDurationText}
              sx={{
                mb: 1,
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
                bgcolor: "primary.main",
                color: "primary.contrastText",
                "& .MuiChip-icon": { color: "primary.contrastText" },
              }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  borderRadius: 1,
                  bgcolor: "divider",
                }}
              />
              <DirectionsBusIcon
                sx={{ color: "primary.main", fontSize: 28 }}
                aria-hidden
              />
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  borderRadius: 1,
                  bgcolor: "divider",
                }}
              />
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.75, textAlign: "center", lineHeight: 1.35 }}
            >
              {t("transferCard.viaHub", {
                city: hubName,
                duration: layoverText,
              })}
            </Typography>
            {(carrier1 || carrier2) && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, textAlign: "center", lineHeight: 1.35 }}
              >
                {[carrier1, carrier2].filter(Boolean).join(" · ")}
              </Typography>
            )}
          </Box>

          {/* Прибуття */}
          <Box sx={{ flex: { sm: 1 }, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography
                component="span"
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {arriveAt.format("HH:mm")}
              </Typography>
              <Chip
                size="small"
                icon={<EventIcon sx={{ fontSize: 14 }} />}
                label={arriveAt.format("DD.MM.YYYY")}
                sx={{
                  height: 24,
                  fontSize: "0.75rem",
                  bgcolor: "action.hover",
                  "& .MuiChip-icon": { color: "primary.main" },
                }}
              />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 0.5 }}>
              {getCityDisplay(segment2.toStop.city)}
            </Typography>
            {segment2.toStop.address && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.8125rem" }}
              >
                <MapAddressLink
                  city={getCityDisplay(segment2.toStop.city)}
                  address={segment2.toStop.address}
                />
              </Typography>
            )}
          </Box>

          {/* Ціна та дія */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "flex-start", sm: "flex-end" },
              justifyContent: "center",
              flex: { sm: "0 0 auto" },
              minWidth: { sm: 140 },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "error.main",
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {t("units.uahAmount", { amount: priceUah })}
            </Typography>
            <Button
              variant="contained"
              onClick={handleSelect}
              fullWidth={isMobile}
              sx={{
                mt: 1.5,
                minWidth: 140,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                py: 1,
              }}
            >
              {t("trips.select")}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TransferItineraryCard;
